// RITA-1552 — Das Aktionsprotokoll nach Verträgen (Skizze Rücklauf vom 21.10.2026, Bilder A und B; Fragen F6, F7, F10
// von Julia „wie empfohlen“ freigegeben; Entscheidungen E2, E4, E5 vom 22.10.2026). Reine Funktionen ohne React: Sie
// ordnen die Einträge des Protokolls den Vertragsblöcken zu und leiten Fortschritt, Status und die Auswahl im Fenster
// „Unterschriebenen Vertrag hochladen“ ab. Die Karte zeigt nur an, was hier entschieden wird.
//
// Zuordnung (Skizze B, „Was mit den Einträgen aus 3c passiert“):
// - Jeder Druck MIT Vertrag (printed mit einem Dokument der Art `contract`) beginnt einen Block; neuester Block zuerst.
// - „Erneut gedruckt“ (reprinted) gehört in den Block seines Drucks (parent_id, auch über eine Rücksendung) und steht
//   dort eingerückt unter ihm; ohne bekannten Druck in den Block „Ohne Vertrag aus dem PRM“.
// - Ein nachgeholtes Anschreiben (printed ohne Vertrag) kommt in den Block des Vertrags, der davor zuletzt gedruckt
//   wurde (rein zeitlich, es gibt keine Spalte dafür); ohne früheren Vertrag in den Block „Ohne Vertrag aus dem PRM“.
// - Scan (scan_uploaded) und Rücksendung (return_printed) gehören über contract_entry_id zu ihrem Vertrag; ohne
//   Vertrag aus dem PRM (null, Fall B) oder mit unbekanntem Vertrag in den Block „Ohne Vertrag aus dem PRM“.
// Fortschritt: Gedruckt (immer) · Unterschrieben zurück (mindestens ein Scan im Block) · Rücksendung (mindestens eine
// Rücksendung im Block). Eine unter „Dokumente“ gelöschte Datei ändert daran nichts (Entscheidung 7).
// Status: Mit Rücksendung „✓ Abgeschlossen am <erste Rücksendung>“ (E2), ohne Scan mit „(ohne Scan)“ (Entscheidung 2) –
// auch wenn der Vertrag danach ersetzt wurde. Sonst ein ersetzter Vertrag „Ersetzt durch Vertrag vom …“ (nur im
// Blockkopf, E5), sonst der nächste Schritt. Bei „Ersetzt“ zeigt der Fortschritt nur die erreichten Schritte.
import type { Druckeintrag } from '../api/vertragsdruck';
import {
  abgeschlossenText,
  CHIP_GEDRUCKT,
  CHIP_RUECKSENDUNG,
  CHIP_UNTERSCHRIEBEN,
  chipText,
  DATEI_GELOESCHT,
  ersetztText,
  NAECHSTER_SCHRITT_HOCHLADEN,
  NAECHSTER_SCHRITT_RUECKSENDUNG,
  neuerAls,
  ScanZusatz,
  vertragsblockTitel,
} from './vertragsdruckTexte';

/** Eine Zeile des Protokolls mit ihren eingerückten „Erneut gedruckt“-Zeilen (neueste zuerst). */
export interface Protokollzeile {
  eintrag: Druckeintrag;
  kinder: Druckeintrag[];
}

export interface Vertragsfortschritt {
  gedruckt: true;
  unterschrieben: boolean;
  ruecksendung: boolean;
}

export type Vertragsstatus =
  /** `datum` = created_at der ersten Rücksendung des Blocks (E2). */
  | { art: 'abgeschlossen'; datum: string; ohneScan: boolean }
  /** `datum` = created_at des ersetzenden Drucks; null, wenn er nicht im Protokoll steht. */
  | { art: 'ersetzt'; datum: string | null }
  | { art: 'naechsterSchrittHochladen' }
  | { art: 'naechsterSchrittRuecksendung' };

export interface Vertragsblock {
  /** Der Druck mit Vertrag, der den Block beginnt. */
  kopf: Druckeintrag;
  /** „<Vertragsvorlage> · gedruckt am <TT.MM.JJJJ>“ */
  titel: string;
  /** Alle Einträge des Blocks, neueste zuerst; „Erneut gedruckt“ unter seinem Druck. */
  zeilen: Protokollzeile[];
  fortschritt: Vertragsfortschritt;
  status: Vertragsstatus;
}

export interface Vertragsbloecke {
  /** Neuester Vertrag zuerst; ab dem zweiten sind es die „älteren Verträge“. */
  bloecke: Vertragsblock[];
  /** Block „Ohne Vertrag aus dem PRM“ (E4: ganz unten, zählt nicht zu den älteren Verträgen); leer = kein Block. */
  ohneVertrag: Protokollzeile[];
}

export type Fortschrittsschritt = 'gedruckt' | 'unterschrieben' | 'ruecksendung';

export interface Fortschrittschip {
  schritt: Fortschrittsschritt;
  /** „✓ Gedruckt“ bzw. „○ Rücksendung“ */
  text: string;
  erreicht: boolean;
}

/** Ein Druck mit Vertrag (beginnt einen Vertragsblock); ein nachgeholtes Anschreiben gehört nicht dazu. */
export const istVertragsdruck = (eintrag: Druckeintrag): boolean =>
  eintrag.entry_type === 'printed' && eintrag.documents.some((dokument) => dokument.kind === 'contract');

/** Sortierung „neueste zuerst“ (bei gleichem Zeitpunkt die höhere Kennung zuerst). */
export function neuesteZuerst(a: Druckeintrag, b: Druckeintrag): number {
  if (neuerAls(a, b)) return -1;
  if (neuerAls(b, a)) return 1;
  return 0;
}

const neuester = (eintraege: Druckeintrag[]): Druckeintrag | null =>
  eintraege.reduce<Druckeintrag | null>(
    (bester, eintrag) => (!bester || neuerAls(eintrag, bester) ? eintrag : bester),
    null,
  );

/**
 * Neuester Druck mit Vertrag (istVertragsdruck), also der Kopf des obersten Vertragsblocks; sonst null. Daran hängen die
 * Rückfrage „Schon ein Vertrag gedruckt“ (Entscheidung 4), das Sperren von „Hochladen“, „Gehört zu“ und der
 * Vertragsbezug der Rücksendung (E6) in Karte und Bearbeiten-Seite. RITA-1552 (Abschlussreview MR 2, S3): aus
 * vertragsdruckTexte.ts hierher verschoben, damit dieselbe Regel gilt wie für die Blockköpfe.
 */
export const letzterVertragsdruck = (eintraege: Druckeintrag[]): Druckeintrag | null =>
  neuester(eintraege.filter(istVertragsdruck));

const aeltester = (eintraege: Druckeintrag[]): Druckeintrag | null =>
  eintraege.reduce<Druckeintrag | null>(
    (bester, eintrag) => (!bester || neuerAls(bester, eintrag) ? eintrag : bester),
    null,
  );

/** Zeilen eines Blocks: „Erneut gedruckt“ unter seinem Druck bzw. seiner Rücksendung, sonst für sich. */
function zeilenBilden(sortiert: Druckeintrag[]): Protokollzeile[] {
  const imBlock = new Map(sortiert.map((eintrag) => [eintrag.id, eintrag]));
  const elternArt = (eintrag: Druckeintrag) =>
    eintrag.parent_id === null ? undefined : imBlock.get(eintrag.parent_id)?.entry_type;
  const istKind = (eintrag: Druckeintrag) =>
    eintrag.entry_type === 'reprinted' && ['printed', 'return_printed'].includes(elternArt(eintrag) ?? '');
  return sortiert
    .filter((eintrag) => !istKind(eintrag))
    .map((eintrag) => ({
      eintrag,
      kinder: sortiert.filter((kind) => istKind(kind) && kind.parent_id === eintrag.id),
    }));
}

function statusDesBlocks(
  kopf: Druckeintrag,
  imBlock: Druckeintrag[],
  unterschrieben: boolean,
  nachId: Map<number, Druckeintrag>,
): Vertragsstatus {
  const ersteRuecksendung = aeltester(imBlock.filter((eintrag) => eintrag.entry_type === 'return_printed'));
  if (ersteRuecksendung)
    return { art: 'abgeschlossen', datum: ersteRuecksendung.created_at, ohneScan: !unterschrieben };
  if (kopf.superseded_by !== null) {
    return { art: 'ersetzt', datum: nachId.get(kopf.superseded_by)?.created_at ?? null };
  }
  return unterschrieben ? { art: 'naechsterSchrittRuecksendung' } : { art: 'naechsterSchrittHochladen' };
}

/** Ordnet alle Einträge den Vertragsblöcken bzw. dem Block „Ohne Vertrag aus dem PRM“ zu (Regeln im Kopf). */
export function vertragsbloecke(eintraege: Druckeintrag[]): Vertragsbloecke {
  const sortiert = [...eintraege].sort(neuesteZuerst);
  const nachId = new Map(sortiert.map((eintrag) => [eintrag.id, eintrag]));
  const koepfe = sortiert.filter(istVertragsdruck);
  const kopfIds = new Set(koepfe.map((kopf) => kopf.id));

  /** Kennung des Vertragsblocks eines Eintrags; null = „Ohne Vertrag aus dem PRM“. */
  const blockVon = (eintrag: Druckeintrag): number | null => {
    let aktuell = eintrag;
    const gesehen = new Set<number>();
    // „Erneut gedruckt“ folgt seinem Druck (auch über mehrere Stufen; ein Kreis führt in „Ohne Vertrag“).
    while (aktuell.entry_type === 'reprinted') {
      if (aktuell.parent_id === null || gesehen.has(aktuell.id)) return null;
      gesehen.add(aktuell.id);
      const eltern = nachId.get(aktuell.parent_id);
      if (!eltern) return null;
      aktuell = eltern;
    }
    if (istVertragsdruck(aktuell)) return aktuell.id;
    const bezug = aktuell;
    if (bezug.entry_type === 'printed') {
      // Nachgeholtes Anschreiben: der zuletzt davor gedruckte Vertrag (Köpfe neueste zuerst).
      return koepfe.find((kopf) => neuerAls(bezug, kopf))?.id ?? null;
    }
    if (bezug.entry_type === 'scan_uploaded' || bezug.entry_type === 'return_printed') {
      return bezug.contract_entry_id !== null && kopfIds.has(bezug.contract_entry_id) ? bezug.contract_entry_id : null;
    }
    return null;
  };

  const jeBlock = new Map<number | null, Druckeintrag[]>();
  sortiert.forEach((eintrag) => {
    const block = blockVon(eintrag);
    jeBlock.set(block, [...(jeBlock.get(block) ?? []), eintrag]);
  });

  const bloecke = koepfe.map((kopf): Vertragsblock => {
    const imBlock = jeBlock.get(kopf.id) ?? [kopf];
    const unterschrieben = imBlock.some((eintrag) => eintrag.entry_type === 'scan_uploaded');
    const ruecksendung = imBlock.some((eintrag) => eintrag.entry_type === 'return_printed');
    return {
      kopf,
      titel: vertragsblockTitel(kopf),
      zeilen: zeilenBilden(imBlock),
      fortschritt: { gedruckt: true, unterschrieben, ruecksendung },
      status: statusDesBlocks(kopf, imBlock, unterschrieben, nachId),
    };
  });
  return { bloecke, ohneVertrag: zeilenBilden(jeBlock.get(null) ?? []) };
}

/** Bild A/B: alle drei Schritte mit ✓/○; bei „Ersetzt“ nur die erreichten (Skizze B: nur „✓ Gedruckt“). */
export function fortschrittsChips(block: Vertragsblock): Fortschrittschip[] {
  const schritte: Array<{ schritt: Fortschrittsschritt; text: string; erreicht: boolean }> = [
    { schritt: 'gedruckt', text: CHIP_GEDRUCKT, erreicht: block.fortschritt.gedruckt },
    { schritt: 'unterschrieben', text: CHIP_UNTERSCHRIEBEN, erreicht: block.fortschritt.unterschrieben },
    { schritt: 'ruecksendung', text: CHIP_RUECKSENDUNG, erreicht: block.fortschritt.ruecksendung },
  ];
  return schritte
    .filter(({ erreicht }) => block.status.art !== 'ersetzt' || erreicht)
    .map(({ schritt, text, erreicht }) => ({ schritt, text: chipText(text, erreicht), erreicht }));
}

/** Text rechts im Blockkopf: nächster Schritt, „✓ Abgeschlossen am …“ oder „Ersetzt durch Vertrag vom …“. */
export function statusText(status: Vertragsstatus): string {
  switch (status.art) {
    case 'abgeschlossen':
      return abgeschlossenText(status.datum, status.ohneScan);
    case 'ersetzt':
      return ersetztText(status.datum ?? '');
    case 'naechsterSchrittRuecksendung':
      return NAECHSTER_SCHRITT_RUECKSENDUNG;
    default:
      return NAECHSTER_SCHRITT_HOCHLADEN;
  }
}

/** E4: Der Block „Ohne Vertrag aus dem PRM“ ist offen, wenn er der einzige Block ist, sonst eingeklappt. */
export const ohneVertragAnfangsOffen = (bloecke: Vertragsbloecke): boolean => bloecke.bloecke.length === 0;

/** Anzahl der „älteren Verträge“ (ohne den neuesten und ohne den Block „Ohne Vertrag aus dem PRM“, E4). */
export const aeltereAnzahl = (bloecke: Vertragsbloecke): number => Math.max(0, bloecke.bloecke.length - 1);

/**
 * „Erneut drucken“ einer Zeile: der nicht ersetzte Druck mit Vertrag und jede Rücksendung (F10; E2: auch mehrere zum
 * selben Vertrag), jeweils nur mit Datei. Nie beim Scan, beim nachgeholten Anschreiben und bei „Erneut gedruckt“.
 */
export function erneutDruckbar(eintrag: Druckeintrag): boolean {
  if (!eintrag.filename) return false;
  if (eintrag.entry_type === 'return_printed') return true;
  return istVertragsdruck(eintrag) && eintrag.superseded_by === null;
}

/** Graue Unterzeilen einer Protokollzeile: nur noch „Datei gelöscht“ (E5: „Ersetzt durch …“ steht im Blockkopf). */
export const zeilenUnterzeilen = (eintrag: Druckeintrag): string[] => (eintrag.filename ? [] : [DATEI_GELOESCHT]);

const zumVertrag = (
  eintraege: Druckeintrag[],
  art: Druckeintrag['entry_type'],
  vertragId: number | null | undefined,
) =>
  vertragId === null || vertragId === undefined
    ? []
    : eintraege.filter((eintrag) => eintrag.entry_type === art && eintrag.contract_entry_id === vertragId);

/** Neuester Scan zum Vertrag (Hinweis „schon ein Scan hochgeladen“, Bild C b); null ohne Scan oder ohne Vertrag. */
export const letzterScanFuer = (eintraege: Druckeintrag[], vertragId: number | null | undefined) =>
  neuester(zumVertrag(eintraege, 'scan_uploaded', vertragId));

/** Gibt es zum Vertrag schon einen Scan? (gelber Hinweis im Fenster „Rücksendung drucken“, Bild D) */
export const scanVorhandenFuer = (eintraege: Druckeintrag[], vertragId: number | null | undefined) =>
  letzterScanFuer(eintraege, vertragId) !== null;

/**
 * Neueste Rücksendung zum Vertrag (Rückfrage „Schon eine Rücksendung gedruckt“, Bild D a). Ohne Vertrag aus dem PRM
 * (null) immer null: Im Fall B gibt es keine Rückfrage (E3).
 */
export const letzteRuecksendungFuer = (eintraege: Druckeintrag[], vertragId: number | null | undefined) =>
  neuester(zumVertrag(eintraege, 'return_printed', vertragId));

export interface ScanOption {
  eintrag: Druckeintrag;
  zusatz: ScanZusatz;
}

export interface ScanAuswahl {
  /** Alle im PRM gedruckten Verträge, neueste zuerst. */
  optionen: ScanOption[];
  /** Kennung der vorausgewählten Option; null ohne Vertrag. */
  vorausgewaehlt: number | null;
}

/**
 * Zusatz eines Vertrags in der Auswahl (Bild C a). Vorrang: Scan vorhanden vor ersetzt vor noch kein Scan. Der Server
 * markiert bei jedem neuen Vertrag ALLE früheren als ersetzt; nur mit diesem Vorrang zeigt ein älterer Vertrag mit Scan
 * „– Scan vorhanden“ wie in der Skizze (Pro Holidays, 20.10.2026, unter einem ersetzten Vertrag). Der Bauplan nannte
 * „ersetzt“ zuerst; die freigegebene Skizze geht vor. „– ersetzt“ steht nur, wenn der Vertrag vor seinem Abschluss
 * ersetzt wurde, also genau beim Blockstatus „Ersetzt durch Vertrag vom …“ (F7, E5; Review-Befund F2, Freigabe Julia
 * 23.10.2026). Ein abgeschlossener und danach ersetzter Vertrag zeigt „– noch kein Scan“ bzw. „– Scan vorhanden“.
 */
const scanZusatz = (block: Vertragsblock): ScanZusatz => {
  if (block.fortschritt.unterschrieben) return 'scanVorhanden';
  return block.status.art === 'ersetzt' ? 'ersetzt' : 'keinScan';
};

/**
 * Bild C a: Auswahl „Zu welchem Vertrag gehört der Scan?“ – alle Drucke mit Vertrag, neueste zuerst (die Köpfe der
 * Vertragsblöcke), mit Zusatz (scanZusatz). Vorausgewählt ist der neueste nicht ersetzte Vertrag ohne Scan, also die
 * oberste Option mit „– noch kein Scan“; gibt es keine, der neueste (Review-Befund F2, Freigabe Julia 23.10.2026).
 * „Ersetzt“ heißt dabei wie beim Zusatz „vor dem Abschluss ersetzt“. Ein einziger Vertrag ist damit immer gewählt.
 */
export function vertragsauswahlFuerScan(eintraege: Druckeintrag[]): ScanAuswahl {
  const optionen = vertragsbloecke(eintraege).bloecke.map(
    (block): ScanOption => ({ eintrag: block.kopf, zusatz: scanZusatz(block) }),
  );
  const vorauswahl = optionen.find((option) => option.zusatz === 'keinScan') ?? optionen[0];
  return { optionen, vorausgewaehlt: vorauswahl ? vorauswahl.eintrag.id : null };
}
